import { deviceServiceStatusChoices } from '@/components/global/options/selectOptions.jsx';
import ActiveIcon from '@/assets/icons/active-icon.svg?react';
import ServiceNewIcon from '@/assets/icons/service-new-icon.svg?react';
import ServiceFailedIcon from '@/assets/icons/service-failed-icon.svg?react';
import ServiceProgressIcon from '@/assets/icons/service-progress-icon.svg?react';
import ServiceInspectionIcon from '@/assets/icons/service-inspection-icon.svg?react';
import { formatIntervalSince } from '@/helpers/date/formatTimeInterval.js';
import { usePermissions, useTranslate } from 'react-admin';
import PropTypes from 'prop-types';
import { DrawerLink } from '@/components/global/drawers/DrawerLink.jsx';

const DeviceListServiceStatusField = ({ record }) => {
    const translate = useTranslate();
    const { permissions, isLoading } = usePermissions();
    if (!record || isLoading) return null;

    const serviceStatus = record.serviceStatus ?? 'waitingForTriage';
    const status = deviceServiceStatusChoices(translate).find(s => s.id === serviceStatus);
    const hasLink = record.activeRequestId && (permissions.isAdmin || permissions.isBok);

    const content = (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '8px' }}>
                <ServiceStatusIcon status={serviceStatus} />
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'left' }}>
                <span>{status.name}</span>
                <SecondaryInfo status={serviceStatus} record={record} />
            </div>
        </div>
    );

    return hasLink ? (
        <DrawerLink
            resource='service-request'
            action='edit'
            source={record.activeRequestId}
            label={content}
            onClick={e => {
                e.stopPropagation();
            }}
            sx={{ cursor: 'pointer', display: 'inline-block' }}
        />
    ) : (
        content
    );
};

export default DeviceListServiceStatusField;

const ServiceStatusIcon = ({ status }) => {
    switch (status) {
        case 'new':
        case 'waitingForTriage':
            return <ServiceNewIcon />;
        case 'failedContact':
            return <ServiceFailedIcon />;
        case 'serviceInspection':
            return <ServiceInspectionIcon />;
        case 'ok':
            return <ActiveIcon />;
        default:
            return <ServiceProgressIcon />;
    }
};

const SecondaryInfo = ({ status, record }) => {
    const translate = useTranslate();
    if (status === 'ok') {
        return null;
    }

    if (status === 'waitingForTriage') {
        const occurredAt = record.issueOccurredAt ? formatIntervalSince(record.issueOccurredAt) : translate('resources.device.fields.unknownTime');
        return <SecondaryText text={occurredAt} />;
    }

    if (status === 'serviceInspection') {
        const plannedServiceDate = calculatePlannedServiceDate(record);
        const daysRemaining = daysToDate(plannedServiceDate);
        const daysText = daysDiffToText(daysRemaining, translate);

        return <SecondaryText text={daysText} />;
    }

    const formattedId = record.activeRequest.customId.toString().padStart(6, '0');

    return <SecondaryText text={`#${formattedId}`} />;
};

const SecondaryText = ({ text }) => (
    <span style={{ fontSize: '11px' }}>
        <strong>{text}</strong>
    </span>
);

const calculatePlannedServiceDate = record => {
    const deploymentDate = new Date(record.deploymentDate);
    const requestCreationDate = new Date(record.activeRequest.createdAt);
    const plannedServiceDate = new Date(requestCreationDate.getFullYear(), deploymentDate.getMonth(), deploymentDate.getDate());
    if (requestCreationDate > plannedServiceDate) {
        plannedServiceDate.setFullYear(plannedServiceDate.getFullYear() + 1);
    }

    return plannedServiceDate;
};

const daysToDate = date => {
    const msPerDay = 1000 * 60 * 60 * 24;
    const now = dateWithoutTime(new Date());
    const toDate = dateWithoutTime(date);

    return Math.round((toDate - now) / msPerDay);
};

const dateWithoutTime = date => new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));

const daysDiffToText = (days, translate) => {
    if (days === 0) return translate('resources.device.fields.today');
    if (days === 1) return translate('resources.device.fields.tomorrow');
    if (days === -1) return translate('resources.device.fields.yesterday');
    if (days < 0) return `${-days} ${translate('resources.device.fields.daysAgo')}`;
    return translate('resources.device.fields.daysFromNow', { days });
};

const RecordModel = PropTypes.shape({
    serviceStatus: PropTypes.string,
    issueOccurredAt: PropTypes.string,
    activeRequestId: PropTypes.string,
    deploymentDate: PropTypes.string,
    activeRequest: PropTypes.shape({
        id: PropTypes.string.isRequired,
        customId: PropTypes.number.isRequired,
        createdAt: PropTypes.string.isRequired,
    }),
});

DeviceListServiceStatusField.propTypes = {
    record: RecordModel,
};

ServiceStatusIcon.propTypes = {
    status: PropTypes.string.isRequired,
};

SecondaryInfo.propTypes = {
    status: PropTypes.string.isRequired,
    record: RecordModel,
};

SecondaryText.propTypes = {
    text: PropTypes.string.isRequired,
};
